import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { calendarAPI, videoEvidenceAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Calendar, CheckCircle, XCircle, Loader2, ExternalLink, Unlink, Zap, ZapOff, Video, Monitor, Shield, Users, ChevronDown, ChevronUp, AlertTriangle, Settings2, Info, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateLongFr } from '../../utils/dateFormat';
import SettingsPageLayout from '../../components/SettingsPageLayout';

export default function Integrations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [googleConnection, setGoogleConnection] = useState(null);
  const [outlookConnection, setOutlookConnection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [connectingOutlook, setConnectingOutlook] = useState(false);
  const [disconnecting, setDisconnecting] = useState(null);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);
  const [autoSyncProviders, setAutoSyncProviders] = useState([]);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [savingAutoSync, setSavingAutoSync] = useState(null);

  // Video provider states
  const [videoProviders, setVideoProviders] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [connectingZoom, setConnectingZoom] = useState(false);
  const [connectingTeams, setConnectingTeams] = useState(false);
  const [zoomForm, setZoomForm] = useState({ zoom_email: '' });
  const [teamsForm, setTeamsForm] = useState({ azure_user_id: '', teams_email: '' });

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const googleResult = searchParams.get('google');
    const outlookResult = searchParams.get('outlook');
    if (googleResult === 'connected') {
      toast.success('Google Calendar connecté avec succès');
      loadAll();
    } else if (googleResult === 'error') {
      toast.error(`Échec Google Calendar (${searchParams.get('reason') || 'erreur'})`);
    }
    if (outlookResult === 'connected') {
      toast.success('Outlook Calendar connecté avec succès');
      loadAll();
    } else if (outlookResult === 'upgraded_teams') {
      toast.success('Teams avancé activé — les réunions Teams utiliseront votre identité Microsoft');
      loadAll();
    } else if (outlookResult === 'error') {
      toast.error(`Échec Outlook Calendar (${searchParams.get('reason') || 'erreur'})`);
    }
    if (googleResult || outlookResult) {
      searchParams.delete('google'); searchParams.delete('outlook'); searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAll = async () => {
    try {
      const [connRes, syncRes, videoRes] = await Promise.all([
        calendarAPI.listConnections(),
        calendarAPI.getAutoSyncSettings(),
        videoEvidenceAPI.providerStatus().catch(() => ({ data: null })),
      ]);
      const connections = connRes.data.connections || [];
      setGoogleConnection(connections.find(c => c.provider === 'google') || null);
      setOutlookConnection(connections.find(c => c.provider === 'outlook') || null);
      setAutoSyncEnabled(syncRes.data.auto_sync_enabled || false);
      setAutoSyncProviders(syncRes.data.auto_sync_providers || []);
      setConnectedProviders(syncRes.data.connected_providers || []);
      if (videoRes.data) setVideoProviders(videoRes.data);
    } catch (error) {
      console.error('Error loading:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleProviderSync = async (provider) => {
    setSavingAutoSync(provider);
    try {
      const newList = autoSyncProviders.includes(provider)
        ? autoSyncProviders.filter(p => p !== provider)
        : [...autoSyncProviders, provider];
      const res = await calendarAPI.updateAutoSyncSettings({ auto_sync_providers: newList });
      setAutoSyncEnabled(res.data.auto_sync_enabled || false);
      setAutoSyncProviders(res.data.auto_sync_providers || []);
      setConnectedProviders(res.data.connected_providers || []);
      const label = provider === 'google' ? 'Google Calendar' : 'Outlook';
      if (newList.includes(provider)) {
        toast.success(`Auto-sync activé pour ${label}`);
      } else {
        toast.success(`Auto-sync désactivé pour ${label}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingAutoSync(null);
    }
  };

  const handleConnect = async (provider) => {
    const setConnecting = provider === 'google' ? setConnectingGoogle : setConnectingOutlook;
    setConnecting(true);
    try {
      const fn = provider === 'google' ? calendarAPI.connectGoogle : calendarAPI.connectOutlook;
      const response = await fn();
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la connexion');
      setConnecting(false);
    }
  };

  const handleDisconnect = async (provider) => {
    setDisconnecting(provider);
    try {
      if (provider === 'google') {
        await calendarAPI.disconnectGoogle();
        setGoogleConnection(null);
        toast.success('Google Calendar déconnecté');
      } else if (provider === 'outlook') {
        await calendarAPI.disconnectOutlook();
        setOutlookConnection(null);
        toast.success('Outlook Calendar déconnecté');
      } else if (provider === 'zoom') {
        await videoEvidenceAPI.disconnectZoom();
        toast.success('Zoom déconnecté');
        loadAll();
      } else if (provider === 'teams') {
        await videoEvidenceAPI.disconnectTeams();
        toast.success('Microsoft Teams déconnecté');
        loadAll();
      }
    } catch (error) {
      toast.error('Erreur lors de la déconnexion');
    } finally {
      setDisconnecting(null);
    }
  };

  const handleConnectZoom = async () => {
    setConnectingZoom(true);
    try {
      await videoEvidenceAPI.connectZoom({ zoom_email: zoomForm.zoom_email || undefined });
      toast.success('Zoom configuré avec succès');
      setExpandedProvider(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de configuration Zoom');
    } finally {
      setConnectingZoom(false);
    }
  };

  const handleConnectTeams = async () => {
    if (!teamsForm.azure_user_id?.trim()) {
      toast.error('L\'identifiant utilisateur Azure AD est requis');
      return;
    }
    setConnectingTeams(true);
    try {
      await videoEvidenceAPI.connectTeams({
        azure_user_id: teamsForm.azure_user_id.trim(),
        teams_email: teamsForm.teams_email || undefined,
      });
      toast.success('Microsoft Teams configuré avec succès');
      setExpandedProvider(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de configuration Teams');
    } finally {
      setConnectingTeams(false);
    }
  };

  // =========================================================
  //  Calendar Provider Card (Google / Outlook — existing logic)
  // =========================================================
  const renderCalendarCard = (provider, connection, connectingState) => {
    const isGoogle = provider === 'google';
    const label = isGoogle ? 'Google Calendar' : 'Outlook / Microsoft 365';
    const email = isGoogle ? connection?.google_email : connection?.outlook_email;
    const testId = isGoogle ? 'google-calendar-card' : 'outlook-calendar-card';
    const isConnected = connection?.status === 'connected';
    const isExpired = connection?.status === 'expired';
    const meetConnected = isGoogle && isConnected;

    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testId} key={provider}>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${isGoogle ? 'bg-blue-50' : 'bg-sky-50'}`}>
              <Calendar className={`w-5 h-5 ${isGoogle ? 'text-blue-600' : 'text-sky-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-slate-900">{label}</h3>
                {isConnected && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
              </div>
              <p className="text-sm text-slate-500">
                Synchronisez vos engagements avec {isGoogle ? 'votre agenda Google' : 'votre calendrier Outlook'}.
              </p>
              {/* Capabilities badges */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                  <Calendar className="w-3 h-3" /> Calendrier
                </span>
                {isGoogle && (
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${meetConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'}`} data-testid="meet-badge">
                    <Video className="w-3 h-3" /> Google Meet
                    {meetConnected && <CheckCircle className="w-3 h-3" />}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {isConnected ? (
          <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-800" data-testid={`${provider}-connected-email`}>{email || 'Compte connecté'}</p>
                  <p className="text-xs text-slate-400">Connecté le {formatDateLongFr(connection.connected_at)}</p>
                </div>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={() => handleDisconnect(provider)}
                disabled={disconnecting === provider}
                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 h-8 text-xs"
                data-testid={`disconnect-${provider}-btn`}
              >
                {disconnecting === provider ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Unlink className="w-3.5 h-3.5 mr-1" />}
                Déconnecter
              </Button>
            </div>
            {isGoogle && meetConnected && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-xs text-emerald-700 flex items-center gap-1">
                  <Video className="w-3 h-3" />
                  Création de liens Google Meet activée via cette connexion.
                </p>
              </div>
            )}
            {!isGoogle && provider === 'outlook' && connection && (
              <>
                {/* Calendar always active */}
                <div className="mt-3 mx-5 mb-1 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="outlook-calendar-active-banner">
                  <div className="flex items-center gap-2.5">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Calendrier Outlook synchronisé</p>
                      <p className="text-sm text-slate-600 mt-0.5">
                        Vos événements Outlook sont utilisés pour la détection de conflits.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Teams scope level */}
                {connection?.has_online_meetings_scope ? (
                  <div className="mx-5 mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="teams-delegated-active-banner">
                    <div className="flex items-center gap-2.5">
                      <Video className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-emerald-900">Teams avancé activé</p>
                        <p className="text-sm text-emerald-700 mt-0.5">
                          Les réunions Teams sont créées sous votre propre identité Microsoft.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mx-5 mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="teams-upgrade-banner">
                    <div className="flex items-start gap-2.5">
                      <Video className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900">Teams avancé (optionnel)</p>
                        <p className="text-sm text-blue-700 mt-0.5">
                          Activez les permissions avancées pour créer les réunions Teams sous votre identité et récupérer les rapports de présence. Réservé aux comptes Microsoft 365 pro.
                        </p>
                        <Button
                          size="sm"
                          onClick={async () => {
                            try {
                              setConnectingOutlook(true);
                              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                              const res = await calendarAPI.upgradeOutlookTeams(tz);
                              if (res.data?.authorization_url) {
                                window.location.href = res.data.authorization_url;
                              }
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Erreur lors de l\'activation Teams');
                            } finally {
                              setConnectingOutlook(false);
                            }
                          }}
                          disabled={connectingOutlook}
                          className="mt-2.5 h-8 text-sm bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="upgrade-teams-btn"
                        >
                          {connectingOutlook ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Video className="w-4 h-4 mr-1.5" />}
                          Activer Teams avancé
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        ) : isExpired ? (
          <div className="border-t border-amber-100 bg-amber-50/50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <XCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-800">Session expirée</p>
                  <p className="text-xs text-slate-400">{email || 'Reconnectez votre compte'}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleConnect(provider)} disabled={connectingState} className="h-8 text-xs" data-testid={`reconnect-${provider}-btn`}>
                  {connectingState ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
                  Reconnecter
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDisconnect(provider)} disabled={disconnecting === provider}
                  className="text-red-500 border-red-200 hover:bg-red-50 h-8 text-xs">
                  <Unlink className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="border-t border-slate-100 px-5 py-3">
            <Button onClick={() => handleConnect(provider)} disabled={connectingState} size="sm" className="h-8 text-xs" data-testid={`connect-${provider}-btn`}>
              {connectingState ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <ExternalLink className="w-3.5 h-3.5 mr-1" />}
              Connecter {label}
            </Button>
          </div>
        )}
      </div>
    );
  };

  // =========================================================
  //  Video Provider Card (Zoom / Teams)
  // =========================================================
  const renderVideoProviderCard = (providerKey) => {
    const info = videoProviders?.[providerKey];
    if (!info) return null;

    const isZoom = providerKey === 'zoom';
    const isTeams = providerKey === 'teams';
    const isMeet = providerKey === 'meet';

    // Google Meet: handled by calendar card above
    if (isMeet) return null;

    const label = info.label || (isZoom ? 'Zoom' : 'Microsoft Teams');
    const isConnected = info.connected;
    const isPlatformConfigured = info.configured;
    const email = info.email;
    const isExpanded = expandedProvider === providerKey;

    const features = info.features || [];
    const canCreate = features.includes('create_meeting');
    const canAttendance = features.includes('fetch_attendance');

    const accentColor = isZoom ? 'blue' : 'violet';
    const iconBg = isZoom ? 'bg-blue-50' : 'bg-violet-50';
    const iconColor = isZoom ? 'text-blue-600' : 'text-violet-600';

    return (
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`${providerKey}-card`} key={providerKey}>
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
              <Monitor className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-slate-900">{label}</h3>
                {isConnected && <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />}
              </div>
              <p className="text-sm text-slate-500">
                {isZoom
                  ? 'Créez des réunions Zoom et récupérez les présences directement depuis NLYT.'
                  : info.level === 'advanced'
                    ? 'Réunion Teams créée automatiquement via votre compte Microsoft Pro.'
                    : info.level === 'standard'
                      ? 'Microsoft Teams disponible via votre compte Outlook.'
                      : 'Connectez votre calendrier Outlook pour activer Microsoft Teams.'
                }
              </p>
              {/* Feature badges */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {isTeams && info.level === 'advanced' && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-violet-50 text-violet-700">
                    <Video className="w-3 h-3" /> Création automatique
                  </span>
                )}
                {isTeams && info.level === 'standard' && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-violet-50 text-violet-700">
                    <Video className="w-3 h-3" /> Création de réunion
                  </span>
                )}
                {isTeams && info.has_attendance && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-50 text-emerald-700">
                    <Users className="w-3 h-3" /> Récupération de présences
                  </span>
                )}
                {isZoom && canCreate && (
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isConnected ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                    <Video className="w-3 h-3" /> Création de réunion
                  </span>
                )}
                {isZoom && canAttendance && (
                  <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    <Users className="w-3 h-3" /> Présences auto
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {isConnected ? (
          <div className="border-t border-slate-100 bg-slate-50/50 px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-800" data-testid={`${providerKey}-connected-email`}>
                    {email || (isPlatformConfigured ? 'Configuré (niveau plateforme)' : 'Connecté')}
                  </p>
                  {info.connected_at && (
                    <p className="text-xs text-slate-400">Connecté le {formatDateLongFr(info.connected_at)}</p>
                  )}
                  {isTeams && info.level === 'advanced' && (
                    <p className="text-xs text-emerald-600 mt-0.5">Réunion Teams créée automatiquement via votre compte Microsoft Pro</p>
                  )}
                  {isTeams && info.level === 'standard' && (
                    <p className="text-xs text-violet-600 mt-0.5">Microsoft Teams disponible via votre compte Outlook</p>
                  )}
                  {isTeams && info.level === 'application' && (
                    <p className="text-xs text-slate-500 mt-0.5">Via identifiant Azure AD (mode application)</p>
                  )}
                  {isPlatformConfigured && !email && !isTeams && (
                    <p className="text-xs text-slate-400">Configuration serveur active</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline" size="sm"
                onClick={() => handleDisconnect(providerKey)}
                disabled={disconnecting === providerKey}
                className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600 h-8 text-xs"
                data-testid={`disconnect-${providerKey}-btn`}
              >
                {disconnecting === providerKey ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Unlink className="w-3.5 h-3.5 mr-1" />}
                Déconnecter
              </Button>
            </div>
            {/* Warning if connected but server credentials missing (application mode only) */}
            {isTeams && info.level === 'application' && !isPlatformConfigured && (
              <div className="flex items-start gap-2 mt-3 p-2 bg-amber-50 border border-amber-200 rounded-md" data-testid="teams-connected-no-server-warning">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Identifiant enregistré. La <strong>création automatique de réunions</strong> ne sera pas disponible tant que les credentials Azure serveur ne sont pas configurés.
                </p>
              </div>
            )}
            {/* Upgrade to advanced — shown for standard level */}
            {isTeams && info.level === 'standard' && !info.has_attendance && (
              <div className="flex items-start gap-2 mt-3 p-2 bg-slate-50 border border-slate-200 rounded-md">
                <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-slate-600">
                  Vous avez un compte Microsoft 365 Pro ? <strong>Activez Teams avancé</strong> dans la section Calendriers pour bénéficier de la récupération automatique des présences.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="border-t border-slate-100 px-5 py-3">
            {isTeams && !isExpanded && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-2.5 bg-slate-50 border border-slate-200 rounded-md">
                  <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-slate-600">
                    Connectez votre calendrier <strong>Outlook</strong> dans la section Calendriers ci-dessus pour activer automatiquement Microsoft Teams.
                  </p>
                </div>
                <button
                  onClick={() => setExpandedProvider(providerKey)}
                  className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
                  data-testid="teams-advanced-config-link"
                >
                  Configuration avancée (Azure AD)
                </button>
              </div>
            )}
            {isZoom && !isExpanded && (
              <Button
                onClick={() => setExpandedProvider(providerKey)}
                size="sm" className="h-8 text-xs"
                data-testid={`connect-${providerKey}-btn`}
              >
                <Settings2 className="w-3.5 h-3.5 mr-1" />
                Configurer {label}
              </Button>
            )}
            {isExpanded && (
              <div className="space-y-3" data-testid={`${providerKey}-config-form`}>
                <p className="text-xs text-slate-600">
                  {isZoom
                    ? 'Configurez votre intégration Zoom. Les credentials serveur (Account ID, Client ID, Client Secret) doivent être configurés par un administrateur.'
                    : 'Renseignez votre identifiant Azure AD pour le mode application Teams.'
                  }
                </p>

                {isZoom && (
                  <div className="space-y-2">
                    <div>
                      <Label htmlFor="zoom-email" className="text-xs text-slate-600">Email Zoom (optionnel)</Label>
                      <Input
                        id="zoom-email"
                        type="email"
                        value={zoomForm.zoom_email}
                        onChange={(e) => setZoomForm({ ...zoomForm, zoom_email: e.target.value })}
                        placeholder="votre@email.com"
                        className="mt-1 h-8 text-sm"
                        data-testid="zoom-email-input"
                      />
                    </div>
                    {!isPlatformConfigured && (
                      <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                          Les credentials Zoom (Account ID, Client ID, Secret) ne sont pas encore configurés sur le serveur.
                          Contactez votre administrateur ou ajoutez-les dans les variables d'environnement.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isTeams && (
                  <div className="space-y-2">
                    {/* Flow clarification */}
                    <div className="flex items-start gap-2 p-2 bg-slate-50 border border-slate-200 rounded-md">
                      <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-slate-600">
                        <strong>Méthode recommandée :</strong> activez <em>Teams avancé</em> via votre connexion Outlook dans la section Calendriers ci-dessus. Ce formulaire est un mode alternatif pour les configurations avancées.
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="teams-azure-id" className="text-xs text-slate-600">Identifiant utilisateur Azure AD</Label>
                      <Input
                        id="teams-azure-id"
                        value={teamsForm.azure_user_id}
                        onChange={(e) => setTeamsForm({ ...teamsForm, azure_user_id: e.target.value })}
                        placeholder="ex: user@votredomaine.com ou UUID Azure AD"
                        className="mt-1 h-8 text-sm"
                        data-testid="teams-azure-id-input"
                      />
                      <p className="text-xs text-slate-400 mt-1">Email Microsoft 365 ou Object ID Azure AD (portal.azure.com)</p>
                    </div>
                    <div>
                      <Label htmlFor="teams-email" className="text-xs text-slate-600">Email Teams (optionnel)</Label>
                      <Input
                        id="teams-email"
                        type="email"
                        value={teamsForm.teams_email}
                        onChange={(e) => setTeamsForm({ ...teamsForm, teams_email: e.target.value })}
                        placeholder="votre@entreprise.com"
                        className="mt-1 h-8 text-sm"
                        data-testid="teams-email-input"
                      />
                    </div>
                    {!isPlatformConfigured && (
                      <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-testid="teams-server-warning">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-amber-700">
                          Les credentials Azure serveur (Tenant ID, Client ID, Secret) ne sont pas encore configurés.
                          Votre identifiant sera enregistré, mais la <strong>création automatique de réunions Teams</strong> ne sera pas disponible tant que la configuration serveur n'est pas finalisée.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm" className="h-8 text-xs"
                    onClick={isZoom ? handleConnectZoom : handleConnectTeams}
                    disabled={isZoom ? connectingZoom : connectingTeams}
                    data-testid={`submit-connect-${providerKey}-btn`}
                  >
                    {(isZoom ? connectingZoom : connectingTeams) ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                    Enregistrer
                  </Button>
                  <Button
                    variant="outline" size="sm" className="h-8 text-xs"
                    onClick={() => setExpandedProvider(null)}
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <SettingsPageLayout
      title="Intégrations"
      breadcrumbLabel="Intégrations"
      description="Connectez vos services pour synchroniser vos calendriers et gérer vos visioconférences."
    >

        {/* ========= SECTION: Calendriers ========= */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-testid="section-calendriers">Calendriers</h2>
          </div>
          <div className="space-y-3">
            {renderCalendarCard('google', googleConnection, connectingGoogle)}
            {renderCalendarCard('outlook', outlookConnection, connectingOutlook)}

            {/* ── Apple Calendar & autres (ICS Export) ── */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="apple-calendar-section">
              <div className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-slate-900 mb-1">Apple Calendar & autres calendriers</h3>
                    <p className="text-sm text-slate-500">
                      Exportez vos engagements au format iCalendar (.ics). Compatible avec Apple Calendar, Thunderbird et tout calendrier standard.
                    </p>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-100 px-5 py-4">
                <div className="flex items-center gap-3 text-sm text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span>
                    Un bouton <strong>"Autres calendriers (.ics)"</strong> est disponible sur chaque engagement et chaque invitation. Le fichier .ics téléchargé s'ouvre directement dans votre application calendrier.
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-3 ml-7">
                  Cet export est manuel. En cas de modification de l'engagement, ajoutez-le à nouveau pour mettre à jour votre calendrier.
                </p>
              </div>
            </div>

            {/* ── Auto-Sync per provider ── */}
            {(googleConnection?.status === 'connected' || outlookConnection?.status === 'connected') && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="auto-sync-section">
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-violet-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-slate-900 mb-1">Auto-sync calendrier</h3>
                      <p className="text-sm text-slate-500">
                        Chaque nouvel engagement sera ajouté automatiquement aux calendriers activés ci-dessous.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-100 px-5 py-4 space-y-2">
                  {googleConnection?.status === 'connected' && (() => {
                    const isOn = autoSyncProviders.includes('google');
                    return (
                      <div className="flex items-center justify-between py-2" data-testid="auto-sync-google-row">
                        <div className="flex items-center gap-2.5">
                          <Calendar className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-slate-800">Google Calendar</span>
                          {isOn && <Zap className="w-3.5 h-3.5 text-violet-600" />}
                        </div>
                        <button
                          onClick={() => handleToggleProviderSync('google')}
                          disabled={savingAutoSync === 'google'}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            isOn ? 'bg-violet-600' : 'bg-slate-200'
                          }`}
                          data-testid="auto-sync-google-toggle"
                        >
                          {savingAutoSync === 'google' ? (
                            <Loader2 className="absolute left-1/2 -translate-x-1/2 w-3.5 h-3.5 animate-spin text-white" />
                          ) : (
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                              isOn ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          )}
                        </button>
                      </div>
                    );
                  })()}
                  {outlookConnection?.status === 'connected' && (() => {
                    const isOn = autoSyncProviders.includes('outlook');
                    return (
                      <div className="flex items-center justify-between py-2" data-testid="auto-sync-outlook-row">
                        <div className="flex items-center gap-2.5">
                          <Calendar className="w-4 h-4 text-sky-600" />
                          <span className="text-sm font-medium text-slate-800">Outlook</span>
                          {isOn && <Zap className="w-3.5 h-3.5 text-violet-600" />}
                        </div>
                        <button
                          onClick={() => handleToggleProviderSync('outlook')}
                          disabled={savingAutoSync === 'outlook'}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            isOn ? 'bg-violet-600' : 'bg-slate-200'
                          }`}
                          data-testid="auto-sync-outlook-toggle"
                        >
                          {savingAutoSync === 'outlook' ? (
                            <Loader2 className="absolute left-1/2 -translate-x-1/2 w-3.5 h-3.5 animate-spin text-white" />
                          ) : (
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                              isOn ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          )}
                        </button>
                      </div>
                    );
                  })()}
                  {autoSyncProviders.length > 0 && (
                    <p className="text-xs text-violet-600 pt-1 flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      Auto-sync actif vers {autoSyncProviders.map(p => p === 'google' ? 'Google Calendar' : 'Outlook').join(' et ')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ========= SECTION: Visioconférence ========= */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Monitor className="w-4 h-4 text-slate-400" />
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider" data-testid="section-visio">Visioconférence</h2>
          </div>

          {/* Explanation */}
          <div className="flex items-start gap-2.5 p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg mb-3">
            <Info className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-indigo-700">
              Connectez un provider de visioconférence pour créer des réunions directement depuis NLYT
              et récupérer automatiquement les rapports de présence après chaque réunion.
            </p>
          </div>

          <div className="space-y-3">
            {renderVideoProviderCard('zoom')}
          </div>

          {/* Google Meet note — tied to Calendar */}
          <div className={`mt-3 flex items-start gap-2.5 p-3 rounded-lg border ${
            googleConnection?.status === 'connected'
              ? 'bg-emerald-50/50 border-emerald-100'
              : 'bg-slate-50 border-slate-200'
          }`} data-testid="meet-calendar-note">
            <Video className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
              googleConnection?.status === 'connected' ? 'text-emerald-600' : 'text-slate-400'
            }`} />
            <div>
              <p className={`text-xs font-medium ${
                googleConnection?.status === 'connected' ? 'text-emerald-800' : 'text-slate-600'
              }`}>Google Meet</p>
              <p className={`text-xs mt-0.5 ${
                googleConnection?.status === 'connected' ? 'text-emerald-700' : 'text-slate-500'
              }`}>
                {googleConnection?.status === 'connected'
                  ? 'Activé via votre connexion Google Calendar. Les liens Google Meet sont créés automatiquement pour vos RDV visio Google.'
                  : 'Connectez Google Calendar ci-dessus pour activer la création automatique de liens Google Meet.'
                }
              </p>
              {googleConnection?.status === 'connected' && (
                <div className="flex items-center gap-1.5 mt-1">
                  <CheckCircle className="w-3 h-3 text-emerald-600" />
                  <span className="text-xs text-emerald-600 font-medium">Création de liens Meet active</span>
                </div>
              )}
            </div>
          </div>

          {/* Microsoft Teams note — tied to Outlook */}
          {(() => {
            const teamsInfo = videoProviders?.teams;
            const teamsLevel = teamsInfo?.level;
            const isActive = teamsLevel === 'advanced';
            const isStandard = teamsLevel === 'standard';
            return (
              <div className={`mt-3 flex items-start gap-2.5 p-3 rounded-lg border ${
                isActive ? 'bg-emerald-50/50 border-emerald-100' : 'bg-slate-50 border-slate-200'
              }`} data-testid="teams-calendar-note">
                <Video className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                  isActive ? 'text-emerald-600' : 'text-slate-400'
                }`} />
                <div>
                  <p className={`text-xs font-medium ${
                    isActive ? 'text-emerald-800' : 'text-slate-600'
                  }`}>Microsoft Teams</p>
                  <p className={`text-xs mt-0.5 ${
                    isActive ? 'text-emerald-700' : 'text-slate-500'
                  }`}>
                    {isActive
                      ? 'Activé via votre connexion Outlook. Les liens Teams sont créés automatiquement pour vos RDV visio Microsoft.'
                      : isStandard
                        ? 'Les liens Teams ne peuvent pas être créés automatiquement pour vos RDV visio Microsoft. Vous avez besoin d\'un compte Microsoft 365 Pro pour ceci.'
                        : 'La création automatique de liens Teams nécessite un compte Microsoft 365 professionnel connecté via Outlook. Connectez un compte Outlook compatible ci-dessus.'
                    }
                  </p>
                  {isActive && (
                    <div className="flex items-center gap-1.5 mt-1">
                      <CheckCircle className="w-3 h-3 text-emerald-600" />
                      <span className="text-xs text-emerald-600 font-medium">Création de liens Teams active</span>
                    </div>
                  )}
                  {isActive && teamsInfo?.has_attendance && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <CheckCircle className="w-3 h-3 text-emerald-600" />
                      <span className="text-xs text-emerald-600 font-medium">Récupération de présences disponible</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
          <p className="text-xs text-amber-700">
            <strong>Alternative :</strong> Exportez chaque engagement en fichier ICS depuis la page de détail (compatible avec tous les calendriers).
          </p>
        </div>
    </SettingsPageLayout>
  );
}
